import { getAuthToken } from '../contexts/AuthContext';

const API_BASE = '/api';

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...options?.headers,
    },
  });
  if (!res.ok) {
    // Auto-logout on 401 — token expired or invalidated by server restart
    if (res.status === 401 && getAuthToken()) {
      localStorage.removeItem('riddim-admin-token');
      window.dispatchEvent(new Event('auth-expired'));
    }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || err.reason || res.statusText || 'request failed');
  }
  return res.json();
}

export const api = {
  // Events
  getEvents: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/events${qs}`);
  },
  getEvent: (id: string) => request<any>(`/events/${id}`),
  createEvent: (data: any) => request<{ id: string }>('/events', { method: 'POST', body: JSON.stringify(data) }),
  updateEvent: (id: string, data: any) => request<any>(`/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEvent: (id: string) => request<any>(`/events/${id}`, { method: 'DELETE' }),
  validateEvent: (id: string) => request<any>(`/events/${id}/validate`, { method: 'POST' }),
  rejectEvent: (id: string) => request<any>(`/events/${id}/reject`, { method: 'POST' }),

  // Artists
  getArtists: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/artists${qs}`);
  },
  getArtist: (id: string) => request<any>(`/artists/${id}`),
  createArtist: (data: any) => request<{ id: string }>('/artists', { method: 'POST', body: JSON.stringify(data) }),
  updateArtist: (id: string, data: any) => request<any>(`/artists/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Venues
  getVenues: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/venues${qs}`);
  },
  getVenue: (id: string) => request<any>(`/venues/${id}`),
  createVenue: (data: any) => request<{ id: string }>('/venues', { method: 'POST', body: JSON.stringify(data) }),

  // Sources
  getSources: () => request<any[]>('/sources'),
  getSource: (id: string) => request<any>(`/sources/${id}`),
  createSource: (data: any) => request<{ id: string }>('/sources', { method: 'POST', body: JSON.stringify(data) }),
  updateSource: (id: string, data: any) => request<any>(`/sources/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateSourceClassification: (id: string, data: { role?: string; country?: string | null; scope?: string; specialist?: boolean }) =>
    request<{ success: boolean; handle: string; locked: boolean }>(`/sources/${id}/classification`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSource: (id: string) => request<any>(`/sources/${id}`, { method: 'DELETE' }),


  // Scraper
  runAllScrapers: () => request<any>('/scraper/run-all', { method: 'POST' }),
  runScraper: (sourceId: string) => request<any>(`/scraper/run/${sourceId}`, { method: 'POST' }),
  getScrapeLogs: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/scraper/logs${qs}`);
  },

  // Flyer OCR upload with SSE progress streaming
  uploadFlyerWithProgress: async (
    file: File,
    onProgress: (pct: number, phase: string) => void,
    signal?: AbortSignal
  ): Promise<{
    title?: string; date?: string; venue?: string; address?: string; city?: string;
    start_time?: string; end_time?: string; price?: string;
    is_free?: boolean; artists?: string[]; image_url?: string;
    raw_text?: string; ocr_source?: string;
  }> => {
    const formData = new FormData();
    formData.append('flyer', file);
    const res = await fetch(`${API_BASE}/scraper/ocr-flyer`, {
      method: 'POST', body: formData,
      headers: { 'Accept': 'text/event-stream', ...authHeaders() },
      signal,
    });
    if (!res.ok) { const err = await res.json().catch(() => ({ error: res.statusText })); throw new Error(err.error || res.statusText); }

    type Result = { title?: string; date?: string; venue?: string; address?: string; city?: string; start_time?: string; end_time?: string; price?: string; is_free?: boolean; artists?: string[]; image_url?: string; raw_text?: string; ocr_source?: string; };
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: Result | undefined;
    let streamError: Error | undefined;

    outer: while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.progress !== undefined) onProgress(data.progress, data.phase);
          if (data.phase === 'done' && data.result) { result = data.result; break outer; }
          if (data.phase === 'error') { streamError = new Error(data.error || 'OCR stream error'); break outer; }
        } catch (err) {
          console.warn('[uploadFlyerWithProgress] SSE parse error:', err, 'line:', line);
        }
      }
    }

    if (streamError) throw streamError;
    if (!result) throw new Error('OCR stream ended without a done or error event');
    return result;
  },

  // Flyer OCR upload (simple, no progress)
  uploadFlyer: async (file: File) => {
    const formData = new FormData();
    formData.append('flyer', file);
    const res = await fetch(`${API_BASE}/scraper/ocr-flyer`, { method: 'POST', body: formData, headers: authHeaders() });
    if (!res.ok) { const err = await res.json().catch(() => ({ error: res.statusText })); throw new Error(err.error || res.statusText); }
    return res.json() as Promise<{
      title?: string; date?: string; venue?: string; city?: string;
      start_time?: string; end_time?: string; price?: string;
      is_free?: boolean; artists?: string[]; image_url?: string;
      raw_text?: string; ocr_source?: string;
    }>;
  },

  // Scraper settings
  getScraperSettings: () => request<{ multi_engine_ocr: boolean }>('/scraper/settings'),
  updateScraperSettings: (data: { multi_engine_ocr: boolean }) => request<any>('/scraper/settings', { method: 'POST', body: JSON.stringify(data) }),

  // Instagram session
  getIgSession: () => request<{ has_session: boolean; valid: boolean; cookie_count: number; expires: string | null; expired_flagged: boolean; login_running: boolean; ig_proxy?: { configured: boolean; proxy: string | null }; scraping_enabled?: boolean }>('/scraper/instagram/session'),
  uploadIgSession: (cookies: any[]) => request<{ success: boolean; cookie_count: number }>('/scraper/instagram/session', { method: 'POST', body: JSON.stringify(cookies) }),
  // Seed the browser session from the proven Belgium-born mobile session (no IG contact).
  seedIgFromMobile: () => request<{ success: boolean; cookie_count: number; user_id?: string }>('/scraper/instagram/seed-from-mobile', { method: 'POST' }),
  // The ONE deliberate IG contact: a single home-feed load via the configured egress.
  validateIgNow: () => request<{ result: 'ok' | 'invalid' | 'skipped' | 'error'; ig_proxy?: { configured: boolean; proxy: string | null } }>('/scraper/instagram/validate-now', { method: 'POST' }),
  startIgLogin: () => request<{ started: boolean; already_running?: boolean }>('/scraper/instagram/start-login', { method: 'POST' }),
  // Interactive login: the SERVER logs in itself (session born on its IP +
  // identity). Credentials are sent once over HTTPS and never stored.
  igInteractiveLoginStart: (username: string, password: string) =>
    request<{ accepted: boolean; reason?: string }>('/scraper/instagram/interactive-login/start', { method: 'POST', body: JSON.stringify({ username, password }) }),
  igInteractiveLoginCode: (code: string) =>
    request<{ accepted: boolean; reason?: string }>('/scraper/instagram/interactive-login/code', { method: 'POST', body: JSON.stringify({ code }) }),
  igInteractiveLoginStatus: () =>
    request<{ state: 'idle' | 'starting' | 'running' | 'awaiting_code' | 'success' | 'failed'; detail: string }>('/scraper/instagram/interactive-login/status'),
  igInteractiveLoginCancel: () =>
    request<{ cancelled: boolean }>('/scraper/instagram/interactive-login/cancel', { method: 'POST' }),

  // SOCKS5 credential management (admin). Status endpoint NEVER returns
  // user/pass — only structural info. POST takes new creds, writes atomically
  // to server .env (mode 0600) + updates process.env in place.
  getSocksStatus: () => request<{ configured: boolean; host: string | null; port: string; last_rotated_at: string | null }>('/scraper/instagram/socks-credentials/status'),
  updateSocksCredentials: (body: { user: string; pass: string; host?: string; port?: string }) =>
    request<{ success: boolean }>('/scraper/instagram/socks-credentials', { method: 'POST', body: JSON.stringify(body) }),

  // Duplicate detection
  getSuspectedDuplicates: () => request<Array<{ id1: string; title1: string; date: string; city: string; venue_id1: string | null; id2: string; title2: string; venue_id2: string | null; similarity: number }>>('/scraper/suspected-duplicates'),

  // Social
  getSocialPosts: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/social${qs}`);
  },

  // Source discovery
  getCandidates: () => request<any[]>('/scraper/candidates'),
  suggestCandidate: (username: string) => request<any>('/scraper/candidates/suggest', { method: 'POST', body: JSON.stringify({ username }) }),
  runDiscovery: () => request<any>('/scraper/discover', { method: 'POST' }),

  // Stats
  getStats: () => request<any>('/stats'),

  // City Radars
  getRadars: () => request<any[]>('/radars'),
  getRadar: (id: string) => request<any>(`/radars/${id}`),
  createRadar: (data: any) => request<any>('/radars', { method: 'POST', body: JSON.stringify(data) }),
  updateRadar: (id: string, data: any) => request<any>(`/radars/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  scanRadar: (id: string) => request<any>(`/scraper/scan-radar/${id}`, { method: 'POST' }),
  discoverRadarSources: (id: string) => request<any>(`/scraper/discover-radar/${id}`, { method: 'POST' }),

  // Validation — flyer match candidates
  getFlyerMatches: () => request<FlyerMatchCandidate[]>('/validation/flyer-matches'),
  mergeFlyerMatch: (id: number, keep: 'a' | 'b' = 'a') => request<any>(`/validation/flyer-matches/${id}/merge`, { method: 'POST', body: JSON.stringify({ keep }) }),
  dismissFlyerMatch: (id: number) => request<any>(`/validation/flyer-matches/${id}/dismiss`, { method: 'POST' }),

  // Validation — rejected events review
  getRejectedEvents: () => request<RejectedEvent[]>('/validation/rejected'),
  permanentDeleteRejected: (id: string) =>
    request<any>(`/validation/rejected/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export interface RejectedEvent {
  id: string;
  title: string;
  date: string;
  city?: string;
  image_url?: string;
  source_url?: string;
  validation_reason?: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  is_free?: boolean;
  price_info?: string;
  start_time?: string;
  end_time?: string;
  venue_name?: string;
  venue_city?: string;
  venue_address?: string;
  source_name?: string;
  source_type?: string;
}

// Festivals (Phase F — admin only)
export const festivalApi = {
  getEditions: (params?: { horizon_days?: number; include_past?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.horizon_days) qs.set('horizon_days', String(params.horizon_days));
    if (params?.include_past) qs.set('include_past', '1');
    const s = qs.toString();
    return request<any[]>(`/festivals/editions${s ? '?' + s : ''}`);
  },
  getEdition: (id: string) => request<any>(`/festivals/editions/${id}`),
  updateEdition: (id: string, data: any) => request<any>(`/festivals/editions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getCandidates: () => request<any[]>('/festivals/candidates'),
  harvestCandidates: () => request<any>('/festivals/candidates/harvest', { method: 'POST' }),
  updateCandidate: (id: string, data: { status: 'rejected' | 'dismissed'; notes?: string }) =>
    request<any>(`/festivals/candidates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  validateEdition: (id: string) =>
    request<any>(`/festivals/editions/${id}/validate`, { method: 'POST' }),
};

export interface FlyerMatchCandidate {
  id: number;
  hamming_distance: number;
  detected_at: string;
  event_a_id: string;
  event_b_id: string;
  a_title: string; a_date: string; a_city?: string; a_image?: string;
  a_status: string; a_source_id?: string;
  a_venue_name?: string; a_source_name?: string;
  b_title: string; b_date: string; b_city?: string; b_image?: string;
  b_status: string; b_source_id?: string;
  b_venue_name?: string; b_source_name?: string;
}
